import { ensureValidAccessToken, getAccessToken } from "@/services/oauth2";
import { createAppRequest, resolveBaseURL } from "@/services/request";
import { BizAppConfig } from "config/application.manifest";
import type { ApiRequestOptions } from "./types/common";
import type { CrmAiBizType } from "./types/ai-biz";
import type {
  AiSummaryHistoryPageResult,
  CrmAiSummaryStreamEvent,
  CrmAiSummaryPanel,
} from "./types/ai-summary";

const BASE_PATH = "/bff/zy-crm/ai-summary";
const getRequest = () => createAppRequest();
const CURRENT_TENANT_UUID_STORAGE_KEY = "currentTenantUuid";

const getTenantUuidFromStorage = () => {
  try {
    return localStorage.getItem(CURRENT_TENANT_UUID_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

const buildStreamUrl = (path: string) => {
  const baseURL = resolveBaseURL(BizAppConfig.appApiUrl);
  if (!baseURL) {
    return path;
  }
  return new URL(path, `${baseURL.replace(/\/$/u, "")}/`).toString();
};

export async function queryCrmAiSummary(
  bizType: CrmAiBizType,
  bizUuid: string,
  options?: ApiRequestOptions,
) {
  const request = getRequest();
  return request<CrmAiSummaryPanel | null>(
    `${BASE_PATH}/${bizType}/${bizUuid}`,
    {
      method: "GET",
      ...(options || {}),
    },
  );
}

export async function createCrmAiSummary(
  bizType: CrmAiBizType,
  bizUuid: string,
  options?: ApiRequestOptions,
) {
  const request = getRequest();
  return request<CrmAiSummaryPanel>(`${BASE_PATH}/${bizType}/${bizUuid}`, {
    method: "POST",
    ...(options || {}),
  });
}

export async function streamCrmAiSummary(
  bizType: CrmAiBizType,
  bizUuid: string,
  options: {
    signal?: AbortSignal;
    onEvent?: (event: CrmAiSummaryStreamEvent) => void;
  } = {},
) {
  const token =
    (await ensureValidAccessToken()) || getAccessToken() || undefined;
  const tenantUuid = getTenantUuidFromStorage();
  const response = await fetch(
    buildStreamUrl(`${BASE_PATH}/${bizType}/${bizUuid}/stream`),
    {
      method: "POST",
      headers: {
        Accept: "application/x-ndjson",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(tenantUuid ? { "x-tenant-id": tenantUuid } : {}),
      },
      signal: options.signal,
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "AI 摘要流式生成失败");
  }

  if (!response.body) {
    throw new Error("AI 摘要流不可用");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const emitEvent = (event: CrmAiSummaryStreamEvent) => {
    options.onEvent?.(event);
    if (event.type === "error") {
      throw new Error(event.message || "AI 摘要流式生成失败");
    }
  };

  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }
    emitEvent(JSON.parse(trimmed) as CrmAiSummaryStreamEvent);
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      processLine(line);
    }
    if (done) {
      break;
    }
  }

  if (buffer.trim()) {
    processLine(buffer);
  }
}

export async function queryCrmAiSummaryHistory(
  bizType: CrmAiBizType,
  bizUuid: string,
  params?: { page?: number; pageSize?: number },
  options?: ApiRequestOptions,
) {
  const request = getRequest();
  return request<AiSummaryHistoryPageResult>(
    `${BASE_PATH}/${bizType}/${bizUuid}/history`,
    {
      method: "GET",
      params,
      ...(options || {}),
    },
  );
}
